import { CircleX } from 'lucide-react';
// @ts-expect-error - Using vendored React-Chrono
import { Chrono } from 'react-chrono';
import { useNavigate } from 'react-router';
import { useMemo } from 'react';

// Using vendored React-Chrono instead of npm package
import { usePhotos } from '@/hooks/usePhotos';
import { useKeys } from '@/hooks/useKeys';
import { Photo } from '@/lib/Photo';
import { Photos } from '@/lib/Photos';

interface TimelineItem {
  title: string;
  cardTitle: string;
  cardSubtitle: string;
  cardDetailedText: string[];
  media: {
    type: 'IMAGE';
    source: {
      url: string;
    };
  };
  url: string;
}

export const TimelinePane = () => {
  const { photos: photoData } = usePhotos();

  // Convert PhotoData to Photo instances
  const photoInstances = useMemo(() => photoData.map(data => Photo.fromApi(data)), [photoData]);
  const photos = useMemo(() => new Photos(photoInstances), [photoInstances]);
  const navigate = useNavigate();

  useKeys([
    ['Escape', () => onCloseClick()]
  ], true);

  const onCloseClick = () => { navigate('/photos'); }

  if (!photos || photos.count === 0) {
    return (
      <div className="flex justify-center items-center w-full h-full bg-white rounded-lg">
        <p className="text-xl text-gray-600">No photos available for timeline</p>
      </div>
    );
  }

  // Transform photos into React-Chrono timeline items
  const timelineItems: TimelineItem[] = photos.all
    .sort((a, b) => a.date.getTime() - b.date.getTime()) // Sort chronologically
    .map((photo: Photo) => ({
      title: photo.mediumDate(),
      cardTitle: photo.title || '',
      cardSubtitle: photo.description || '',
      cardDetailedText: [
        photo.locationString() || '',
        photo.user?.name ? `${photo.user.name}` : ''
      ].filter(Boolean),
      media: {
        type: 'IMAGE' as const,
        source: {
          url: photo.imageUrl
        }
      },
      url: photo.url()
    }));

  return (
    <div className="relative w-full h-[calc(100vh-6rem)] flex flex-col bg-white rounded-lg overflow-hidden">
      <button
        onClick={onCloseClick}
        className="flex absolute top-2 right-2 z-10 justify-center items-center w-8 h-8 text-2xl text-white rounded-full cursor-pointer hover:opacity-75"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      >
        <CircleX className="w-8 h-8" />
      </button>

      <div className="overflow-y-auto flex-1 p-6">
        <div className="w-full h-full">
          <Chrono
            items={timelineItems}
            mode="HORIZONTAL"
            disableToolbar={false}
            allowDynamicUpdate={true}
            cardHeight={200}
            theme={{
              primary: '#3B82F6',
              secondary: '#EFF6FF',
              cardBgColor: '#FFFFFF',
              cardForeColor: '#1F2937',
              titleColor: '#1F2937',
              titleColorActive: '#3B82F6'
            }}
            fontSizes={{
              cardSubtitle: '0.875rem',
              cardText: '0.875rem',
              cardTitle: '1.125rem',
              title: '1rem'
            }}
            onItemSelect={(item: any) => {
              if (item.url) {
                navigate(item.url);
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default TimelinePane;
